// Anita Week 2 / Day 3 — Tables, Data and Choice (U.S. Grade 3).
// Math: weekly reading total + average, two-step nims-after-spend,
// compare two strategies (5×4 vs 7×3), 1/4 of a table. Russian RU 1–2.
// Logic: small-and-fast vs many-and-slow trade-off.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-2',
  dayId: 'day-3',
  status: 'active',
  title: 'Таблицы, данные и выбор / Tables, Data and Choice',
  shortTitle: 'Таблицы и выбор / Tables and Choice',
  theme:
    'Таблица помогает увидеть результат и выбрать лучший вариант. / A table helps you see the result and pick the best option.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни приносит таблицу чтения. Сегодня Анита считает сумму и среднее, сравнивает две стратегии и берёт 1/4 от таблицы. / Nimny brings a reading table. Today Anita finds a total and average, compares two strategies, and takes 1/4 of a table.',
  goals: [
    'Считать сумму и среднее по таблице. / Find total and average from a table.',
    'Решать двухшаговые задачи о нимах. / Solve two-step nim problems.',
    'Сравнивать две стратегии. / Compare two strategies.',
    'Брать 1/4 от 32. / Take 1/4 of 32.',
    'Выбирать удобный вариант и объяснять.'
  ],
  vocabulary: [
    { term: 'таблица / table', definition: 'клетки с числами и словами. / cells with numbers and words.' },
    { term: 'всего / total', definition: 'сумма всех чисел. / sum of all numbers.' },
    { term: 'больше / more', definition: 'когда чисел или вещей больше. / when there is a higher number.' },
    { term: 'меньше / less', definition: 'когда чисел или вещей меньше. / when there is a lower number.' },
    { term: 'средний / average', definition: 'сумма ÷ количество строк. / sum ÷ number of rows.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words', type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: таблица, всего, больше, меньше, средний.',
      ruPrimaryText: 'Прочитай по-русски: таблица, всего, больше, меньше, средний.',
      enHelpText: 'Read in Russian: table, total, more, less, average.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения 5 русских слов.'
    },
    {
      id: 'reading-sentences', type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. Таблица помогает увидеть результат.\n2. Я считаю сумму и среднее.\n3. Иногда меньше заданий по больше нимов — это удобнее.\n4. Я выбираю и объясняю.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про таблицы.',
      enHelpText:
        '1. A table helps me see the result.\n2. I find total and average.\n3. Sometimes fewer tasks with higher nims is easier.\n4. I choose and explain.',
      answer: 'Попытка чтения 4 русских фраз.'
    },
    {
      id: 'math-1-reading-table',
      type: 'math',
      title: 'Math 1 — Таблица чтения / Reading Table',
      prompt:
        'Анита читала:\nПонедельник — 12 минут\nВторник — 18 минут\nСреда — 15 минут\nЧетверг — 15 минут\n\nСколько минут всего?\nСколько в среднем за день?\n\nЗапиши ответ через /: всего / в среднем. Например: 60 / 15.',
      ruPrimaryText: '12 + 18 + 15 + 15 = ?. Среднее = сумма ÷ 4.',
      enHelpText:
        'Mon 12, Tue 18, Wed 15, Thu 15. Total? Average? Format as total / average.',
      helper: 'Сначала сумма, потом сумма ÷ 4. / Sum first, then divide by 4.',
      answer: '60 / 15'
    },
    {
      id: 'math-2-nims-after-spend',
      type: 'math',
      title: 'Math 2 — Нимы за задания / Nims After Spending',
      prompt:
        'За одно задание Анита получает 3 нима.\nОна сделала 8 заданий.\nПотом потратила 7 нимов на наклейку.\nСколько нимов осталось?',
      ruPrimaryText: '3 × 8 = ?. ? − 7 = ?',
      enHelpText: '3 nims × 8 tasks. Then she spends 7 on a sticker. How many remain?',
      helper: 'Сначала умножь, потом вычти. / Multiply first, then subtract.',
      answer: '17',
      unit: 'нимов / nims'
    },
    {
      id: 'math-3-compare-strategies',
      type: 'math',
      title: 'Math 3 — Сравни стратегии / Compare Strategies',
      prompt:
        'Вариант A: 5 заданий по 4 нима.\nВариант B: 7 заданий по 3 нима.\nКакой вариант даёт больше нимов и на сколько?\n\nЗапиши ответ через /: какой / на сколько. Например: B / 1.',
      ruPrimaryText: 'A = 5 × 4. B = 7 × 3. Какой больше и на сколько?',
      enHelpText: 'A: 5 × 4. B: 7 × 3. Which gives more nims and by how many? Format as letter / number.',
      helper: 'Перемножь и сравни. / Multiply each, then compare.',
      answer: 'B / 1'
    },
    {
      id: 'math-4-quarter-of-table',
      type: 'math',
      title: 'Math 4 — Четверть таблицы / Quarter of the Table',
      prompt:
        'В таблице 32 карточки.\n1/4 карточек — с животными.\nОстальные — с предметами.\nСколько карточек с предметами?',
      ruPrimaryText: '1/4 от 32 = ?. 32 − ? = ?',
      enHelpText: '32 cards. 1/4 of them have animals. Rest have objects. How many objects?',
      helper: '1/4 от 32 = 32 ÷ 4. / 1/4 of 32 = 32 ÷ 4.',
      answer: '24',
      unit: 'карточек / cards'
    },
    {
      id: 'logic-fewer-vs-more-tasks',
      type: 'text',
      title: 'Логика — что удобнее / Logic — What Is Easier',
      prompt:
        'Что лучше: меньше заданий по 4 нима или больше заданий по 3 нима?\nКогда какой вариант удобнее?\nОтветь 1–2 предложения по-русски.',
      ruPrimaryText: 'Меньше × больше или больше × меньше? Когда какой?',
      enHelpText: 'Fewer tasks at 4 nims, or more tasks at 3 nims? When does each fit? Answer in 1–2 Russian sentences.',
      placeholder: 'A удобнее, когда ... B удобнее, когда ...',
      answer: 'Принимается любой ответ с осмысленным сравнением (например: «A — если хочется отдохнуть быстрее»; «B — если задания лёгкие и интересные»).'
    },
    {
      id: 'spelling-table',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши: таблица, всего, больше, меньше.\nВставь буквы: таб_ица, в_его, бо_ьше, ме_ьше.',
      ruPrimaryText: 'Напиши слова и вставь буквы.',
      enHelpText: 'Write and fill in: таблица, всего, больше, меньше.',
      answer: 'таблица, всего, больше, меньше.'
    },
    {
      id: 'creative-my-table',
      type: 'text',
      title: 'Моя таблица-проект / My Table-Project',
      prompt:
        'Заполни свою таблицу за 4 дня (Пн–Чт). Сколько минут я читала каждый день?\nПн ___ мин\nВт ___ мин\nСр ___ мин\nЧт ___ мин\nСумма: ___\nСреднее (÷ 4): ___',
      ruPrimaryText: 'Заполни таблицу-проект и посчитай сумму и среднее.',
      enHelpText: 'Fill in your 4-day table, then compute total and average.',
      answer: 'Open-ended. Принимается, если сумма правильная и среднее = сумма ÷ 4.'
    },
    {
      id: 'speech-explain-table',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: «Таблица помогает мне ...».',
      ruPrimaryText: 'Скажи по-русски одну фразу про таблицу.',
      enHelpText: 'Say in Russian: "A table helps me …".',
      answer: 'Принимается любая осмысленная русская фраза.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши 5 слов дня.',
    '2. Реши Math 1–4 с шагами.',
    '3. Ответь, какой вариант удобнее и когда.',
    '4. Напиши 4 слова на правописание.',
    '5. Заполни свою таблицу-проект.',
    '6. Скажи родителю: «Таблица помогает мне …».'
  ],
  printWorksheet: {
    title: 'Таблицы, данные и выбор / Tables, Data and Choice',
    subtitle: 'Неделя 2 · День 3 / Week 2 · Day 3',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита читает таблицу, считает среднее и сравнивает две стратегии. / Mission.' },
          { type: 'list', items: [
            'Слова дня: таблица, всего, больше, меньше, средний. / Words.',
            'Маленькое чтение — 4 короткие фразы про таблицы (см. экран). / Reading.',
            'English help мелким шрифтом. / English help.'
          ] }
        ]
      },
      {
        title: 'Математика таблиц / Table Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги и единицы. / Show steps and units.' },
          { type: 'list', items: [
            'Math 1. 12 + 18 + 15 + 15 = ___ минут всего. Среднее = ___ ÷ 4 = ___ минут в день. / Reading total + average.',
            'Math 2. 3 × 8 = ___; ___ − 7 = ___ нимов осталось. / Nims after spending.',
            'Math 3. A = 5 × 4 = ___; B = 7 × 3 = ___; больше ___ на ___. / Compare strategies.',
            'Math 4. 1/4 от 32 = 32 ÷ 4 = ___; предметы = 32 − ___ = ___ карточек. / Quarter of table.',
            'Логика: меньше × больше или больше × меньше — когда какой план удобнее? / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, моя таблица-проект и parent check / Spelling, My Table-Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши таблица, всего, больше, меньше. Вставь буквы: таб_ица, в_его, бо_ьше, ме_ьше. / Spelling.',
            'Заполни мою таблицу-проект за 4 дня (Пн–Чт) с минутами чтения. Сумма ___, среднее ___ (÷ 4). / Fill in the table-project.',
            'Скажи родителю: «Таблица помогает мне …». / Say in Russian.',
            'Parent Check: сумма и среднее, × для умножения, 1/4 от 32, сравнение стратегий. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита читает таблицу, считает сумму и среднее, сравнивает две стратегии и берёт 1/4 от 32.',
    mathGoal: 'U.S. Grade 3: сумма + ÷ для среднего, × как первый шаг, сравнение произведений 5×4 vs 7×3, дробь 1/4 как ÷ 4.',
    russianGoal: 'Прочитать слова: таблица, всего, больше, меньше, средний.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 4 слова, заполнить таблицу-проект.',
    practicalGoal: 'Сравнить две стратегии и выбрать.',
    whatThisLessonTeaches: 'Анита учится извлекать смысл из таблицы: сумма, среднее, сравнение, доля.',
    keyMessage: 'Таблица помогает увидеть и сравнить. Среднее — это сумма, поделённая поровну.',
    howToExplain: 'Покажите простую таблицу. Сложите по столбцу. Поделите на число строк.',
    exampleDialogue: 'Parent: Сколько ты читала за 4 дня всего?\nChild: 60.\nParent: А в среднем за день?\nChild: 60 ÷ 4 = 15.',
    readingSupport: 'Слово «средний» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Таблица помогает мне выбрать.»',
    mathAnswerKey:
      'Math 1: 12 + 18 + 15 + 15 = 60; 60 ÷ 4 = 15.\nMath 2: 3 × 8 = 24; 24 − 7 = 17.\nMath 3: A = 5 × 4 = 20; B = 7 × 3 = 21; B − A = 1.\nMath 4: 1/4 от 32 = 8; 32 − 8 = 24.',
    spellingAnswerKey: 'таблица\nвсего\nбольше\nменьше',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 5 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про таблицы.\nSolution: Каждая фраза — отдельно.\nParent note: Английский — только для смысла.' },
      { taskId: 'math-1-reading-table', answer: 'Answer: 60 / 15.\nSolution: 12 + 18 + 15 + 15 = 60; 60 ÷ 4 = 15.\nParent note: Среднее = «разделить поровну».' },
      { taskId: 'math-2-nims-after-spend', answer: 'Answer: 17 нимов.\nSolution: 3 × 8 = 24; 24 − 7 = 17.\nParent note: Двухшаговая задача × + −.' },
      { taskId: 'math-3-compare-strategies', answer: 'Answer: B / 1.\nSolution: A = 20; B = 21; 21 − 20 = 1.\nParent note: Сравнение двух стратегий.' },
      { taskId: 'math-4-quarter-of-table', answer: 'Answer: 24 карточки.\nSolution: 1/4 от 32 = 8; 32 − 8 = 24.\nParent note: Дробь как часть от общего количества.' },
      { taskId: 'logic-fewer-vs-more-tasks', answer: 'Answer: оба варианта могут быть удобными.\nSolution: A — если хочется отдохнуть быстрее; B — если задания лёгкие и интересные.\nParent note: Принимается любое осмысленное сравнение.' },
      { taskId: 'spelling-table', answer: 'Answer: таблица, всего, больше, меньше.\nSolution: Вставленные буквы: л, с, л, н.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-my-table', answer: 'Answer: 4-дневная таблица с правильным средним.\nSolution: Среднее = сумма ÷ 4.\nParent note: Главное — арифметика правильная.' },
      { taskId: 'speech-explain-table', answer: 'Answer: «Таблица помогает мне …».\nSolution: Любая осмысленная русская фраза.\nParent note: Главное — мысль.' }
    ],
    check: [
      'Анита прочитала русские слова и 4 фразы.',
      'Анита решила Math 1–4 с показом шагов.',
      'Анита сравнила два варианта и объяснила.',
      'Анита написала 4 слова и заполнила свою таблицу-проект.',
      'Анита сказала фразу про таблицу.'
    ],
    spellingCheck: 'Проверить буквы в словах таблица, всего, больше, меньше.',
    readingCheck: 'Засчитывать попытку чтения.',
    practicalCheck: 'Анита понимает, что разные стратегии дают разный итог.',
    drawingCriteria: 'Таблица заполнена за 4 дня, сумма правильная, среднее = сумма ÷ 4.',
    commonMistakes: [
      '«Среднее = последний день.» Правильно: сумма ÷ количество строк.',
      '«Больше заданий = всегда больше нимов.» Не всегда: важна и цена за задание.',
      '«1/4 = 4.» Правильно: 1/4 от 32 = 8 (÷ 4).'
    ],
    discussionPrompts: [
      'Что показывает таблица сегодня?',
      'Когда удобно много маленьких заданий?',
      'Когда удобно меньше, но больших?'
    ],
    ifEasy: 'Среднее за 5 дней: 8, 10, 12, 14, 16. (Ответ: 60 / 5 = 12.)',
    ifDifficult: 'Оставьте Math 1 и Math 3. Math 3 разберите по строкам.',
    rewardGuidance: 'Базовая награда 3 нима. Бонус за самостоятельный расчёт среднего или за объяснение выбора.',
    parentTheoryCard: {
      title: 'Таблицы и среднее (Day 3) / Tables and Average',
      purpose: 'Научить ребёнка читать таблицу и понимать смысл среднего.',
      keyMessage: 'Среднее = сумма ÷ количество строк.',
      parentScript: 'Покажите таблицу. Сложите столбец. Поделите на 4. Скажите: «Если бы ты читала ровно одинаково каждый день, получилось бы 15 минут».',
      practiceSteps: ['Прочитайте 4 фразы.', 'Решите Math 1–4 со шагами.', 'Сравните A и B.', 'Заполните 4-дневную таблицу.'],
      doNotSay: ['Не говорите «ты плохо читаешь».', 'Не сравнивайте с одноклассниками.', 'Не торопите.'],
      paperOnlyNotes: 'Таблица — учебная. Не используйте её как «дневник для родителя».'
    },
    parentOnlyNotes: 'Цифры в таблице ребёнок может выбрать сам.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'average-math', label: 'Бонус за самостоятельный расчёт среднего', amount: 1 },
      { id: 'compare-strategies', label: 'Бонус за сравнение двух стратегий', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words', 'reading-sentences',
      'math-1-reading-table', 'math-2-nims-after-spend',
      'math-3-compare-strategies', 'math-4-quarter-of-table',
      'logic-fewer-vs-more-tasks', 'spelling-table',
      'creative-my-table', 'speech-explain-table'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-2-day-4'
};

export default day3;
